import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, tap, withLatestFrom } from 'rxjs/operators';
import { of, from } from 'rxjs';
import { DispositionService } from '../../../core/services/disposition.service';
import { DispositionActions } from './disposition.actions';
import { Router } from '@angular/router';
import {
  IApiResponse,
  ICreateDispositionResponse,
  IDisposition,
  IDispositionListFilter,
  IDispositionListSort,
} from '../../models/disposition.model';
import { Store } from '@ngrx/store';
import { getQueries } from './disposition.selectors';
import { AlertService } from 'src/app/core/services/alert.service';

@Injectable()
export class DispositionEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly dispositionService: DispositionService,
    private readonly router: Router,
    private readonly store: Store,
    private readonly alertService: AlertService
  ) {}

  loadDispositions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DispositionActions.loadDispositions),
      withLatestFrom(this.store.select(getQueries)),
      mergeMap(([, filters]) => {
        const filter: IDispositionListFilter = filters.body?.filter ?? {};
        const limit = 10;
        const sort: IDispositionListSort = filters.body?.sort ?? {
          key: 'createdAt',
          order: 'desc',
        };
        return from(
          this.dispositionService.getDispositions(
            filters.params?.page,
            limit,
            filter,
            sort
          )
        ).pipe(
          map((data) => {
            return DispositionActions.loadDispositionsSuccess({
              response: data,
              filter: filters.body?.filter ?? {},
            });
          }),
          catchError((error) =>
            of(
              DispositionActions.loadDispositionsFailure({
                error: error.message,
              })
            )
          )
        );
      })
    )
  );

  createDisposition$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DispositionActions.createDisposition),
      mergeMap((action) =>
        this.dispositionService.createDisposition(action.disposition).pipe(
          map((response) => {
            if (response.message == 'DISPOSITION_NAME_ALREADY_EXIST') {
              const data = response.data as ICreateDispositionResponse;

              this.alertService.error(
                response?.message,
                'Message',
                data.created.length > 0
                  ? data.created.map((e) => `'${e.disposition_name}'`).join(', ')
                  : '0',
                data.existing.map((e) => `'${e}'`).join(', ')
              );
            } else {
              const data = response.data as IDisposition;
              this.alertService.success(
                response?.message,
                'Success',
                data.disposition_name
              );
            }
            return DispositionActions.createDispositionSuccess({ response });
          }),
          catchError((error) => {
            return of(
              DispositionActions.createDispositionFailure({
                error: error.message,
              })
            );
          })
        )
      )
    )
  );

  loadSingleDisposition$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DispositionActions.loadSingleDisposition),
      mergeMap(({ id }) =>
        this.dispositionService.getDisposition(id).pipe(
          map((response) =>
            DispositionActions.loadSingleDispositionSuccess({ response })
          ),
          catchError((error) =>
            of(
              DispositionActions.loadSingleDispositionFailure({
                error: error.message,
              })
            )
          )
        )
      )
    )
  );
  updateDisposition$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DispositionActions.updateDisposition),
      mergeMap(
        (
          action // Use mergeMap as it's typically suited for update operations
        ) =>
          from(
            this.dispositionService.updateDisposition(
              action.id,
              action.disposition
            )
          ).pipe(
            map((response: IApiResponse<IDisposition>) => {
              action.closeModalSignal.set(true);
              this.alertService.success(
                response?.message,
                'Success',
                response.data.disposition_name
              );
              return DispositionActions.updateDispositionSuccess({
                response: response,
              });
            }),
            catchError((error) => {
              return of(
                DispositionActions.updateDispositionFailure({
                  error: error.message,
                })
              );
            })
          )
      )
    )
  );

  deleteDisposition$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DispositionActions.deleteDisposition),
      mergeMap((action) =>
        this.dispositionService.deleteDisposition(action.id).pipe(
          map((response) => {
            action.closeModalSignal.set(true); // Trigger the modal close
            this.alertService.success(
              response?.message,
              'Success',
              response.data.disposition_name
            );
            return DispositionActions.deleteDispositionSuccess({
              id: action.id,
              message: response.message,
            });
          }),
          catchError((error) => {
            return of(
              DispositionActions.deleteDispositionFailure({
                error: error.message,
              })
            );
          })
        )
      )
    )
  );

  loadDispositionNames$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DispositionActions.loadDispositionNames),
      mergeMap(() =>
        this.dispositionService.listDispositionName().pipe(
          map((response) => {
            return DispositionActions.loadDispositionNamesSuccess({
              response: response,
            });
          }),
          catchError((error) =>
            of(
              DispositionActions.loadDispositionNamesFailure({
                error: error.message,
              })
            )
          )
        )
      )
    )
  );

  loadAllStatusDispositionNames$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DispositionActions.loadAllStatusDispositionNames),
      mergeMap(() =>
        this.dispositionService.listAllStatusDispositionNames().pipe(
          map((response) => {
            return DispositionActions.loadAllStatusDispositionNamesSuccess({
              response: response,
            });
          }),
          catchError((error) =>
            of(
              DispositionActions.loadAllStatusDispositionNamesFailure({
                error: error.message,
              })
            )
          )
        )
      )
    )
  );

  redirectAfterCreate$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DispositionActions.createDispositionSuccess),
        tap(() => {
          this.router.navigate(['/dispositions']);
        })
      ),
    { dispatch: false }
  );

  refreshDataOnChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        DispositionActions.createDispositionSuccess,
        DispositionActions.updateDispositionSuccess,
        DispositionActions.deleteDispositionSuccess,
        DispositionActions.bulkDeleteDispositionsSuccess,
        DispositionActions.changeDispositionStatusSuccess,
        DispositionActions.createDispositionFailure
      ),
      mergeMap(() => [DispositionActions.loadDispositions()])
    )
  );
  changeStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DispositionActions.changeDispositionStatus),
      mergeMap((action) =>
        from(this.dispositionService.toggleStatus(action.id)).pipe(
          map((response: IApiResponse<IDisposition>) => {
            this.alertService.success(
              response.message,
              'Success',
              response.data.disposition_name
            );
            return DispositionActions.changeDispositionStatusSuccess();
          }),
          catchError((error) =>
            of(
              DispositionActions.changeDispositionStatusFailure({
                error: error.message,
              })
            )
          )
        )
      )
    )
  );

  bulkDeleteDispositions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DispositionActions.bulkDeleteDispositions),
      mergeMap((action) =>
        from(this.dispositionService.bulkDeleteDispositions(action.ids)).pipe(
          map((response) => {
            action.closeModalSignal.set(true);
            this.alertService.success(
              response.message,
              'Success',
              `Deleted ${action.ids.length} disposition(s)`
            );
            return DispositionActions.bulkDeleteDispositionsSuccess({
              ids: action.ids,
            });
          }),
          catchError((error) =>
            of(
              DispositionActions.bulkDeleteDispositionsFailure({
                error: error.message,
              })
            )
          )
        )
      )
    )
  );
}
